// stores/cartStore.js
import { defineStore } from "pinia";
import { useAuthStore } from "~/stores/authStore";

export const useCartStore = defineStore(
  "aanzaUserCartStore", 
  {
  state: () => ({
    items: [],
    isReady: false,
  }),

  getters: {
    // Helper to get total quantity for a Product ID (sum of all variations)
    getProductQuantity: (state) => (productId, variationId = null) => {
      if (variationId) {
        const item = state.items.find(
          (i) => i.id === productId && i.variation?.id === variationId
        );
        return item ? item.quantity : 0;
      }
      return state.items
        .filter((i) => i.id === productId)
        .reduce((total, item) => total + item.quantity, 0);
    },
    // Calculate total cost
    totalCost: (state) => {
      return state.items.reduce(
        (total, item) => total + (item.offer_price || item.price) * item.quantity,
        0,
      );
    },
    // Helper to get total count of all items (optional, for header badge)
    totalItems: (state) => {
      return state.items.reduce((total, item) => total + item.quantity, 0);
    },
  },

  actions: {
    async initializeCart() {
      try {
        this.isReady = true;
      } catch (error) {
        console.error("Failed to init cart", error);
        this.isReady = true;
      }
    },

    addToCart(product, variation = null, quantity = 1) {
      const existingItem = this.items.find((item) => {
        if (variation) {
          return item.id === product.id && item.variation?.id === variation.id;
        }
        return item.id === product.id && !item.variation;
      });

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        // Add new item with quantity
        const itemToAdd = { 
          ...product, 
          quantity: quantity,
          variation: variation || null,
          // Use variation price if available
          price: variation ? (variation.price || product.price) : product.price,
          offer_price: variation ? (variation.offer_price || variation.price || product.offer_price) : product.offer_price
        };
        this.items.push(itemToAdd);
      }
    },

    removeFromCart(productId, variationId = null) {
      const existingItemIndex = this.items.findIndex((item) => {
        if (variationId) {
          return item.id === productId && item.variation?.id === variationId;
        }
        return item.id === productId && !item.variation;
      });

      if (existingItemIndex !== -1) {
        const item = this.items[existingItemIndex];
        item.quantity--;

        if (item.quantity <= 0) {
          this.items.splice(existingItemIndex, 1);
        }
      }
    },

    clearCart() {
      this.items = [];
    },

    async confirmOrder(shopId = null, tableId = null, userData = null) {
      const auth = useAuthStore();
      const finalShopId = shopId || auth.shopId;
      const finalTableId = tableId || auth.tableId;

      if (!finalShopId || !finalTableId) {
        const missing = !finalShopId ? "Shop" : "Table";
        throw new Error(`${missing} ID is missing.`);
      }

      const payload = {
        shop_id: finalShopId,
        order_type: "table",
        shop_table_id: finalTableId,
        // Map local cart items to the format your backend needs
        products: this.items.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          product_variation_id: item.variation ? item.variation.id : null,
        })),
        ...(userData || {})
      };

      try {
        const config = useRuntimeConfig();
        const url = config.public.apiBase;

        const response = await $fetch(url + "/user/order", {
          method: "POST",
          body: payload,
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + auth.token,
          },
        });
        
        console.log(response.message);
        this.clearCart();
        return response;
      } catch (err) {
        const msg = err.data?.message || "Order creation failed";
        console.error(msg);
        throw err;
      }
    },
  },
  persist: true,
});
